import React, { useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { FaPaperPlane, FaTimes, FaPaperclip, FaTrash } from 'react-icons/fa';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { uploadBookingFile } from '../../../utils/uploadBookingFile';
import '../styles/PromotionalEmailModal.css';

const GET_COMPANY = `
  query GetCompany($id: String!) {
    company(id: $id) {
      companyName
      companyLogo
    }
  }
`;

const SEND_PROMOTIONAL = `
  mutation SendPromotionalEmail($input: SendPromotionalEmailInput!) {
    sendPromotionalEmail(input: $input) {
      queued
      skipped
      message
    }
  }
`;

function resolveUploadUrl(rawUpload) {
  if (!rawUpload) return '';
  const value = String(rawUpload).trim();
  if (!value) return '';
  if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:')) return value;
  const bucket = process.env.REACT_APP_AWS_BUCKET_NAME;
  const region = process.env.REACT_APP_AWS_REGION;
  if (bucket && region) {
    return `https://${bucket}.s3.${region}.amazonaws.com/${value}`;
  }
  return '';
}

/**
 * Branded customer email. Bulk mode (Customers list) picks selected or all
 * customers; with `recipient` it emails that one customer (customer profile,
 * document Forward). `initial` pre-fills subject / headline / message /
 * attachments, e.g. a forwarded proposal with its PDF attached.
 */
export default function PromotionalEmailModal({
  customers = [],
  selectedIds = new Set(),
  onClose,
  onSent,
  recipient = null,
  initial = null,
  title,
  embedded = false,
  onSaveDraft,
}) {
  const [form, setForm] = useState({
    subject: initial?.subject || '',
    headline: initial?.headline || '',
    message: initial?.message || '',
    ctaLabel: initial?.ctaLabel || '',
    ctaUrl: initial?.ctaUrl || '',
    sendToAll: false,
  });
  const [companyName, setCompanyName] = useState(
    Cookies.get('companyName') || 'SAS Cleaning Suite',
  );
  const [companyLogoUrl, setCompanyLogoUrl] = useState('');
  const [attachments, setAttachments] = useState(() =>
    Array.isArray(initial?.attachments) ? initial.attachments : [],
  );
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const selectedCount = recipient ? 1 : selectedIds.size;
  const recipientCount = recipient
    ? 1
    : form.sendToAll
      ? customers.length
      : selectedCount;
  const greetingName = recipient
    ? String(recipient.fullName || '').trim().split(/\s+/)[0] || 'Customer'
    : 'Customer';

  useEffect(() => {
    const companyId = Cookies.get('companyId');
    if (!companyId) return;
    graphqlRequest(GET_COMPANY, { id: companyId })
      .then((res) => {
        const company = res?.company;
        if (company?.companyName) setCompanyName(company.companyName);
        const logo = resolveUploadUrl(company?.companyLogo);
        if (logo) setCompanyLogoUrl(logo);
      })
      .catch(() => {});
  }, []);

  const previewMessage = useMemo(() => {
    const personalized = form.message.replace(
      /\{\{\s*name\s*\}\}/gi,
      greetingName,
    );
    return personalized.replace(/\n/g, '<br/>');
  }, [form.message, greetingName]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    if (attachments.length + files.length > 10) {
      setError('You can attach up to 10 files.');
      return;
    }

    try {
      setUploading(true);
      setError('');
      // Through the API rather than a presigned PUT: the bucket has no CORS rule.
      const uploaded = await Promise.all(
        files.map(async (file) => {
          const stored = await uploadBookingFile(file);
          const isImage = stored.contentType.startsWith('image/');
          return { ...stored, previewUrl: isImage ? URL.createObjectURL(file) : null };
        }),
      );

      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to upload attachment(s).');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (key) => {
    setAttachments((prev) => prev.filter((item) => item.key !== key));
  };

  const handleSend = async () => {
    if (!form.subject.trim() || !form.headline.trim() || !form.message.trim()) {
      setError('Subject, headline, and message are required.');
      return;
    }
    if (!recipient && !form.sendToAll && selectedCount === 0) {
      setError('Select at least one customer or choose "All customers".');
      return;
    }

    try {
      setSending(true);
      setError('');
      const res = await graphqlRequest(SEND_PROMOTIONAL, {
        input: {
          subject: form.subject.trim(),
          headline: form.headline.trim(),
          message: form.message.trim(),
          ctaLabel: form.ctaLabel.trim() || null,
          ctaUrl: form.ctaUrl.trim() || null,
          sendToAll: recipient ? false : form.sendToAll,
          customerIds: recipient
            ? [recipient.id]
            : form.sendToAll
              ? null
              : Array.from(selectedIds),
          attachments: attachments.map(({ key, fileName, contentType }) => ({
            key,
            fileName,
            contentType,
          })),
        },
      });

      onSent?.(res.sendPromotionalEmail);
      if (!embedded) onClose();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to queue promotional email.');
    } finally {
      setSending(false);
    }
  };

  const formBody = (
        <>
        {embedded ? null : (
        <div className="promo-email-header">
          <div>
            <h2>{title || (recipient ? `Email ${recipient.fullName || 'customer'}` : 'Promotional Email')}</h2>
            <p>
              Branded email with SAS navbar, your company logo, optional CTA button,
              and file attachments. Use {'{{name}}'} in the message for personalization.
            </p>
          </div>
          <button type="button" className="promo-email-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        )}

        <div className="promo-email-body">
          <div className="promo-email-form">
            {recipient ? (
              <div className="promo-recipient-row promo-recipient-single">
                <span>To:</span>
                <strong>{recipient.fullName || 'Customer'}</strong>
                <span className="promo-recipient-email">{recipient.email}</span>
              </div>
            ) : (
            <div className="promo-recipient-row">
              <label className="promo-radio">
                <input
                  type="radio"
                  name="recipientMode"
                  checked={!form.sendToAll}
                  onChange={() => setForm((p) => ({ ...p, sendToAll: false }))}
                />
                Selected customers ({selectedCount})
              </label>
              <label className="promo-radio">
                <input
                  type="radio"
                  name="recipientMode"
                  checked={form.sendToAll}
                  onChange={() => setForm((p) => ({ ...p, sendToAll: true }))}
                />
                All customers ({customers.length})
              </label>
            </div>
            )}

            <label>
              Subject
              <input
                name="subject"
                value={form.subject}
                onChange={handleChange}
                placeholder="Summer cleaning offer — 20% off"
              />
            </label>

            <label>
              Headline
              <input
                name="headline"
                value={form.headline}
                onChange={handleChange}
                placeholder="Exclusive deal for our valued clients"
              />
            </label>

            <label>
              Message
              <textarea
                name="message"
                rows={7}
                value={form.message}
                onChange={handleChange}
                placeholder="Write your promotional message here. Use {{name}} is optional — we personalize with each customer's name automatically."
              />
            </label>

            <div className="promo-cta-row">
              <label>
                Button label (optional)
                <input
                  name="ctaLabel"
                  value={form.ctaLabel}
                  onChange={handleChange}
                  placeholder="Book now"
                />
              </label>
              <label>
                Button URL (optional)
                <input
                  name="ctaUrl"
                  value={form.ctaUrl}
                  onChange={handleChange}
                  placeholder="https://yoursite.com/offer"
                />
              </label>
            </div>

            <div className="promo-attachments">
              <div className="promo-attachments-head">
                <span>
                  <FaPaperclip /> Attachments (PDF, images)
                </span>
                <label className="promo-upload-btn">
                  {uploading ? 'Uploading...' : 'Add files'}
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleFileSelect}
                    disabled={uploading || sending}
                  />
                </label>
              </div>
              {attachments.length > 0 && (
                <ul className="promo-attachment-list">
                  {attachments.map((file) => (
                    <li key={file.key}>
                      {file.previewUrl ? (
                        <img
                          src={file.previewUrl}
                          alt=""
                          className="promo-attachment-thumb"
                        />
                      ) : (
                        <span className="promo-attachment-icon">
                          {String(file.fileName || '').match(/\.([a-z0-9]+)$/i)?.[1]?.toUpperCase() ||
                            'FILE'}
                        </span>
                      )}
                      <span>{file.fileName}</span>
                      <small>{file.sizeLabel}</small>
                      <button
                        type="button"
                        onClick={() => removeAttachment(file.key)}
                        aria-label="Remove attachment"
                      >
                        <FaTrash />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {error && <p className="promo-error">{error}</p>}

            <div className="promo-actions">
              {onSaveDraft ? (
                <button
                  type="button"
                  className="promo-cancel"
                  onClick={() => onSaveDraft({ ...form, attachments })}
                >
                  Save draft
                </button>
              ) : null}
              <button type="button" className="promo-cancel" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="promo-send"
                onClick={handleSend}
                disabled={sending || uploading || recipientCount === 0}
              >
                <FaPaperPlane />
                {sending
                  ? 'Queueing...'
                  : recipient
                    ? `Send to ${recipient.fullName || recipient.email}`
                    : `Send to ${recipientCount} customer${recipientCount === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>

          <div className="promo-email-preview">
            <div className="promo-preview-label">Live preview</div>
            <div className="promo-preview-card">
              <img
                src={`${process.env.PUBLIC_URL || ''}/emailNavbar.png`}
                alt="SAS Cleaning Suite"
                className="promo-preview-banner"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = `${process.env.PUBLIC_URL || ''}/logoSas.png`;
                }}
              />
              <div className="promo-preview-inner">
                <div className="promo-preview-brand">{companyName}</div>
                <div className="promo-preview-sub">Special offer & updates</div>
                {form.subject && (
                  <p className="promo-preview-subject">{form.subject}</p>
                )}
                <hr />
                {companyLogoUrl && (
                  <img
                    src={companyLogoUrl}
                    alt={companyName}
                    className="promo-preview-logo"
                  />
                )}
                <p className="promo-preview-greeting">Dear {greetingName},</p>
                <div className="promo-preview-headline-box">
                  <h3>{form.headline || 'Your headline appears here'}</h3>
                </div>
                <p
                  className="promo-preview-message"
                  dangerouslySetInnerHTML={{
                    __html:
                      previewMessage ||
                      'Your promotional message will appear here.',
                  }}
                />
                {form.ctaLabel && form.ctaUrl && (
                  <span className="promo-preview-cta">{form.ctaLabel}</span>
                )}
                {attachments.length > 0 && (
                  <p className="promo-preview-files">
                    {attachments.length} file
                    {attachments.length === 1 ? '' : 's'} attached
                  </p>
                )}
                <div className="promo-preview-footer">
                  <span>Need assistance?</span>
                  <small>support@sascleaningsuite.com · +61 431 768 538</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
  );

  if (embedded) return <div className="promo-email-modal promo-email-modal--embedded">{formBody}</div>;

  return (
    <div className="promo-email-overlay" onClick={onClose}>
      <div className="promo-email-modal" onClick={(e) => e.stopPropagation()}>
        {formBody}
      </div>
    </div>
  );
}
